// clientsApi — adaptador del CRM del manager sobre lealify-base (Inc5-7).
// Mapea las respuestas de base (contacts / loyalty / analytics) al shape que ya
// esperan los componentes del panel (visitorName/phoneNumber/points/birthDay…),
// para re-cablear tocando lo mínimo. Reemplaza ElasticsearchService* + escrituras
// Firestore de whatsappNumbers/pointsRecharges/redemptions (ahora server-side atómico).
import { baseApi } from './baseClient';
import { splitStoredPhone } from '../lib/countries';
import type { WhatsAppClient, CheckIn } from '../types/user';

// ── Shape que consumen los componentes (= el viejo doc whatsappNumbers / cliente ES) ──
export interface UIClient {
  id: string;
  visitorName: string;
  phoneNumber: string;     // 10 dígitos locales (base guarda así; el +52 es decorativo)
  countryCode: string;     // base no lo separa → '52' por defecto
  points: number;
  email: string | null;
  birthDay: number | null;
  birthMonth: number | null;   // 1-12
  gender: string | null;       // base aún no captura género → null (TODO)
  lastVisit: string | null;
  createdAt: string | null;
  referralCode: string | null;
  tags: string[];
  branchId?: string | null;
  archived?: boolean;
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));

// Mapea una fila de /contacts list (first_name/last_name/phone/points_balance…).
export function mapContactRow(r: any): UIClient {
  const name = [r.first_name, r.last_name].filter(Boolean).join(' ').trim();
  const bd = r.birthdate ? new Date(r.birthdate) : null;
  return {
    id: String(r.id),
    visitorName: name || r.name || 'Cliente',
    phoneNumber: splitStoredPhone(r.phone).national,
    countryCode: splitStoredPhone(r.phone).cc || '52',
    points: num(r.points_balance),
    email: r.email || null,
    birthDay: r.birth_day != null ? Number(r.birth_day) : (bd ? bd.getUTCDate() : null),
    birthMonth: r.birth_month != null ? Number(r.birth_month) : (bd ? bd.getUTCMonth() + 1 : null),
    gender: null,
    lastVisit: r.last_visit_at || null,
    createdAt: r.created_at || null,
    referralCode: r.referral_code || null,
    tags: r.tags || [],
    archived: !!r.archived,
  };
}

// Mapea un item de /analytics/lists/:type ({id, name, phone, email, points_balance, last_visit_at, birthdate?}).
export function mapSegmentItem(r: any): UIClient {
  const bd = r.birthdate ? new Date(r.birthdate) : null;
  return {
    id: String(r.id),
    visitorName: r.name || 'Cliente',
    phoneNumber: splitStoredPhone(r.phone).national,
    countryCode: splitStoredPhone(r.phone).cc || '52',
    points: num(r.points_balance),
    email: r.email || null,
    birthDay: r.birth_day != null ? Number(r.birth_day) : (bd ? bd.getUTCDate() : null),
    birthMonth: r.birth_month != null ? Number(r.birth_month) : (bd ? bd.getUTCMonth() + 1 : null),
    gender: null,
    lastVisit: r.last_visit_at || null,
    createdAt: r.created_at || null,
    referralCode: null,
    tags: [],
  };
}

export interface PagedClients { clients: UIClient[]; total: number; page: number; }

// Mapea un contacto de base al shape WhatsAppClient (ficha del cliente / gestión de puntos).
export function mapToWhatsAppClient(r: any): WhatsAppClient {
  const bd = r.birthdate ? new Date(r.birthdate) : null;
  return {
    id: String(r.id),
    phoneNumber: splitStoredPhone(r.phone).national,
    visitorName: [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.name || null,
    businessId: '',
    createdAt: r.created_at ? new Date(r.created_at) : new Date(),
    lastVisit: r.last_visit_at ? new Date(r.last_visit_at) : null,
    countryCode: splitStoredPhone(r.phone).cc || '52',
    gender: '',                 // base no captura género aún
    points: num(r.points_balance),
    updatedAt: r.updated_at ? new Date(r.updated_at) : new Date(),
    email: r.email || null,
    birthDay: r.birth_day != null ? Number(r.birth_day) : (bd ? bd.getUTCDate() : null),
    birthMonth: r.birth_month != null ? Number(r.birth_month) : (bd ? bd.getUTCMonth() + 1 : null), // 1-12
    marketingOptOut: !!r.marketing_opt_out,
    archived: r.is_active === 0 || !!r.archived,
    avatarUrl: r.avatar_url || null,
    tags: Array.isArray(r.tags) ? r.tags : undefined,
  };
}

// Fila del feed "Ajustes y Bonos" / "Operaciones Recientes" (movimiento de saldo).
export interface MovementRow {
  id: string;
  points: number;            // magnitud (siempre positiva)
  operation: 'add' | 'subtract';
  previousPoints: number;
  newPoints: number;
  createdAt: Date;
  source?: string;
  description?: string;
}
// Fila de "Operaciones Recientes" (ajuste con datos del contacto).
export interface RecentAdjustment {
  id: string;
  clientId: string;
  clientName: string | null;
  clientPhone: string;
  points: number;
  previousPoints: number;
  newPoints: number;
  operation: 'add' | 'subtract';
  createdAt: Date;
  businessId: string;
}
function mapMovement(m: any): MovementRow {
  const delta = num(m.points_delta);
  const after = m.balance_after != null ? num(m.balance_after) : 0;
  return {
    id: String(m.id),
    points: Math.abs(delta),
    operation: delta >= 0 ? 'add' : 'subtract',
    newPoints: after,
    previousPoints: parseFloat((after - delta).toFixed(2)),
    createdAt: m.occurred_at ? new Date(m.occurred_at) : new Date(),
    source: m.source || m.type || undefined,
    description: m.note || undefined,
  };
}

// ── Cliente enriquecido para las vistas de ranking/inactivos del CRM ──
// Espejo del shape que esperaban esas páginas del doc ES (visitCount/lifetime/etc.).
export interface RankClient {
  id: string;
  visitorName: string | null;
  phoneNumber: string;
  countryCode: string;
  email: string | null;
  points: number;            // saldo actual (points_balance)
  visitCount: number | null; // nº de visitas (solo best-clients)
  lifetimePoints: number | null; // puntos históricos otorgados (solo best-clients)
  tags: string[];
  birthDay: number | null;
  birthMonth: number | null;
  gender: string;            // base aún no captura género
  lastVisit: string | null;
  createdAt: string | null;
  businessId: string;
  registrationBranchId?: string;
  registrationBranchName?: string;
}

// Mapea una fila de /analytics/best-clients|inactive-clients al shape RankClient.
function mapRankRow(r: any): RankClient {
  const bd = r.birthdate ? new Date(r.birthdate) : null;
  return {
    id: String(r.id),
    visitorName: r.name || null,
    phoneNumber: splitStoredPhone(r.phone).national,
    countryCode: splitStoredPhone(r.phone).cc || '52',
    email: r.email || null,
    points: num(r.points_balance),
    visitCount: r.visit_count != null ? Number(r.visit_count) : null,
    lifetimePoints: r.lifetime_points != null ? Number(r.lifetime_points) : null,
    tags: Array.isArray(r.tags) ? r.tags : [],
    birthDay: r.birth_day != null ? Number(r.birth_day) : (bd ? bd.getUTCDate() : null),
    birthMonth: r.birth_month != null ? Number(r.birth_month) : (bd ? bd.getUTCMonth() + 1 : null),
    gender: '',
    lastVisit: r.last_visit_at || null,
    createdAt: r.created_at || null,
    businessId: '',
  };
}

// Fecha local → 'YYYY-MM-DD' (sin desfase de zona, a diferencia de toISOString).
function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Fila del feed de visitas (= el viejo doc de visita ES, con cliente/sucursal ya resueltos) ──
export interface VisitFeedRow {
  id: string;
  clientId: string;
  clientName: string | null;
  clientPhone: string;
  countryCode: string;
  branchId?: string;
  branchName?: string;
  checkInDate: string;       // occurred_at (ISO)
  earnedPoints: number;      // points_delta de la visita
  feedback?: string;
  amount?: number | null;    // monto de la venta (transaction_amount)
  ticketRef?: string;        // folio del ticket
  userName?: string;         // quién la registró
  cancelledAt?: string;      // compra cancelada (10-oct-2026)
  cancelReason?: string;
}

// Mapea una fila de /analytics/recent-visits al shape VisitFeedRow.
function mapVisitRow(r: any): VisitFeedRow {
  return {
    id: String(r.id),
    clientId: String(r.contact_id),
    clientName: r.name || null,
    clientPhone: splitStoredPhone(r.phone).national,
    countryCode: splitStoredPhone(r.phone).cc || '52',
    branchId: r.branch_id != null ? String(r.branch_id) : undefined,
    branchName: r.branch_name || undefined,
    checkInDate: r.occurred_at ? String(r.occurred_at) : '',
    earnedPoints: num(r.points_delta),
    feedback: r.feedback || undefined,
    amount: r.transaction_amount != null ? num(r.transaction_amount) : null,
    ticketRef: r.ticket_ref || undefined,
    userName: r.user_name || undefined,
    cancelledAt: r.cancelled_at ? String(r.cancelled_at) : undefined,
    cancelReason: r.cancel_reason || undefined,
  };
}

export const clientsApi = {
  // ── Listado / búsqueda (Inc5) ──
  list: async (opts: { q?: string; tag?: string; page?: number; size?: number; archived?: 'exclude' | 'only' | 'all'; sort?: 'name' | 'last_visit' | 'recent' } = {}): Promise<PagedClients> => {
    const p = new URLSearchParams();
    if (opts.q) p.set('q', opts.q);
    if (opts.tag) p.set('tag', opts.tag);
    if (opts.archived && opts.archived !== 'exclude') p.set('archived', opts.archived);
    if (opts.sort) p.set('sort', opts.sort);
    p.set('page', String(opts.page || 1));
    p.set('limit', String(opts.size || 50));
    const r = await baseApi.get<{ contacts: any[]; total: number; page: number }>(`/contacts?${p}`);
    return { clients: (r.contacts || []).map(mapContactRow), total: r.total || 0, page: r.page || 1 };
  },

  // Buscar por teléfono exacto (lookup del kiosko) → estado de cuenta del cliente.
  lookup: async (phone: string): Promise<UIClient | null> => {
    const digits = phone.replace(/\D/g, '');
    const r = await baseApi.get<{ found: boolean; contact?: any }>(`/loyalty/lookup?phone=${encodeURIComponent(digits)}`);
    return r.found && r.contact ? mapContactRow(r.contact) : null;
  },

  get: async (id: string): Promise<UIClient> => {
    const r = await baseApi.get<{ contact: any }>(`/contacts/${id}`);
    return mapContactRow(r.contact);
  },

  // ── Ficha del cliente (Inc9b) — contacto completo en shape WhatsAppClient ──
  getClient: async (id: string): Promise<WhatsAppClient> => {
    const r = await baseApi.get<{ contact: any }>(`/contacts/${id}`);
    return mapToWhatsAppClient(r.contact);
  },

  // Historial de VISITAS paginado (ficha del cliente). Devuelve CheckIn[] + total + hasMore.
  visitsPaged: async (id: string, page = 1, size = 10): Promise<{ items: CheckIn[]; total: number; hasMore: boolean }> => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('limit', String(size));
    const r = await baseApi.get<{ visits: any[]; total: number; hasMore: boolean }>(`/loyalty/contacts/${id}/visits?${p}`);
    const items: CheckIn[] = (r.visits || []).map((v) => ({
      id: String(v.id),
      checkInDate: v.occurred_at ? new Date(v.occurred_at) : new Date(0),
      earnedPoints: num(v.points_delta),
      feedback: v.feedback || undefined,
      businessId: '',
      totalPointsAfterCheckIn: v.balance_after != null ? num(v.balance_after) : undefined,
      branchName: v.branch_name || undefined,
      amount: v.transaction_amount != null ? num(v.transaction_amount) : null,
      ticketRef: v.ticket_ref || undefined,
      userName: v.user_name || undefined,
      cancelledAt: v.cancelled_at ? String(v.cancelled_at) : undefined,
      cancelReason: v.cancel_reason || undefined,
      cancelledByName: v.cancelled_by_name || undefined,
      accrualBlockReason: v.accrual_block_reason || undefined,
    }));
    return { items, total: r.total || 0, hasMore: !!r.hasMore };
  },

  // Movimientos de saldo NO-visita de un contacto ("Ajustes y Bonos").
  movements: async (id: string, limit = 20): Promise<MovementRow[]> => {
    const r = await baseApi.get<{ movements: any[] }>(`/loyalty/contacts/${id}/movements?limit=${limit}`);
    return (r.movements || []).map(mapMovement);
  },

  // Ajustes manuales recientes de TODO el negocio ("Operaciones Recientes").
  recentAdjustments: async (limit = 10): Promise<RecentAdjustment[]> => {
    const r = await baseApi.get<{ items: any[] }>(`/loyalty/recent-adjustments?limit=${limit}`);
    return (r.items || []).map((m) => {
      const mv = mapMovement(m);
      return {
        id: mv.id,
        clientId: String(m.contact_id),
        clientName: m.client_name || null,
        clientPhone: String(m.client_phone || ''),
        points: mv.points,
        previousPoints: mv.previousPoints,
        newPoints: mv.newPoints,
        operation: mv.operation,
        createdAt: mv.createdAt,
        businessId: '',
      };
    });
  },

  // Baja/alta de marketing (opt-out) y archivar/desarchivar (reversibles).
  setMarketingOptOut: (id: string, optOut: boolean) => baseApi.put(`/contacts/${id}`, { marketing_opt_out: optOut }),
  setArchived: (id: string, archived: boolean) => baseApi.put(`/contacts/${id}`, { archived }),

  // ── Foto del cliente (módulo PERFIL compartido) ──
  // URL servible de la foto (la sirve lealify-base con sesión; el <img> manda la cookie por ser mismo-origen).
  avatarSrc: (id: string, ver?: number) => `${baseApi.baseUrl}/avatars/contact/${id}${ver ? `?v=${ver}` : ''}`,
  // El operador sube/toma la foto. imageDataUrl ya viene comprimida (≤512px) del front por el límite ~1mb del body.
  uploadAvatar: (id: string, imageDataUrl: string) =>
    baseApi.post<{ avatar_url: string }>(`/contacts/${id}/avatar`, { image: imageDataUrl }),

  // Etiquetas puntuales (add/remove) — para la ficha del cliente.
  addTag: (id: string, tag: string) => baseApi.post(`/contacts/${id}/tags`, { tag }),
  removeTag: (id: string, tag: string) => baseApi.del(`/contacts/${id}/tags/${encodeURIComponent(tag)}`),

  // ── Etiquetas a nivel negocio (TagManager) ──
  // Lista de etiquetas distintas con su conteo. Backend devuelve {tag, n}.
  tags: async (): Promise<{ name: string; count: number }[]> => {
    const r = await baseApi.get<{ tags: { tag: string; n: number }[] }>('/contacts/tags');
    return (r.tags || []).map((t) => ({ name: t.tag, count: Number(t.n) || 0 }));
  },
  // Asigna/quita UNA etiqueta a MUCHOS contactos de un golpe (server-side, atómico).
  bulkTag: (tag: string, contactIds: string[], op: 'add' | 'remove'): Promise<{ ok: boolean; affected: number }> =>
    baseApi.post('/contacts/tags/bulk', { tag, op, contact_ids: contactIds.map(Number) }),
  // Borra la etiqueta de TODOS los contactos del negocio.
  deleteTagFromAll: (tag: string): Promise<{ ok: boolean; affected: number }> =>
    baseApi.del(`/contacts/tags/${encodeURIComponent(tag)}`),

  // ── Ranking de mejores clientes (Inc7) ── /analytics/best-clients enriquecido.
  // sort: points(saldo) | lifetime(histórico) | frequency(nº visitas); period: all|30d|90d|12m.
  bestClients: async (opts: { term?: string; page?: number; size?: number; sort?: 'points' | 'lifetime' | 'frequency'; period?: 'all' | '30d' | '90d' | '12m' } = {}): Promise<{ clients: RankClient[]; total: number }> => {
    const p = new URLSearchParams();
    if (opts.term) p.set('term', opts.term);
    p.set('page', String(opts.page || 1));
    p.set('size', String(opts.size || 20));
    if (opts.sort) p.set('sort', opts.sort);
    if (opts.period) p.set('period', opts.period);
    const r = await baseApi.get<{ items: any[]; total: number }>(`/analytics/best-clients?${p}`);
    return { clients: (r.items || []).map(mapRankRow), total: r.total || 0 };
  },

  // ── Clientes inactivos (Inc7) ── /analytics/inactive-clients por rango de última visita.
  // before = "inactivo desde" (cota superior); after = cota inferior opcional (banda). Fechas Date.
  inactiveClients: async (opts: { term?: string; page?: number; size?: number; sort?: 'recent' | 'value'; before?: Date | null; after?: Date | null; minPoints?: number | null } = {}): Promise<{ clients: RankClient[]; total: number; totalPoints: number }> => {
    const p = new URLSearchParams();
    if (opts.term) p.set('term', opts.term);
    p.set('page', String(opts.page || 1));
    p.set('size', String(opts.size || 20));
    if (opts.sort) p.set('sort', opts.sort);
    if (opts.before) p.set('before', ymd(opts.before));
    if (opts.after) p.set('after', ymd(opts.after));
    if (opts.minPoints != null) p.set('min_points', String(opts.minPoints));
    const r = await baseApi.get<{ items: any[]; total: number; total_points: number }>(`/analytics/inactive-clients?${p}`);
    return { clients: (r.items || []).map(mapRankRow), total: r.total || 0, totalPoints: num(r.total_points) };
  },

  // ── Feed de visitas del negocio (Inc7) ── /analytics/recent-visits, cronológico + búsqueda.
  // Cancelar una compra con motivo (dueño/gerente). Quita su cashback aunque el saldo quede negativo.
  cancelVisit: (id: string, reason: string) =>
    baseApi.post<{ balance: number; negative: boolean; removed: number }>(`/loyalty/visits/${id}/cancel`, { reason }),

  // Excel COMPLETO de movimientos (todos los tipos, folio, quién atendió, hora del negocio). Dueño/gerente.
  exportMovements: (opts: { from: string; to: string; branchId?: string }) => {
    const p = new URLSearchParams({ from: opts.from, to: opts.to });
    if (opts.branchId) p.set('branch_id', opts.branchId);
    return baseApi.download(`/loyalty/movements.xlsx?${p}`, `movimientos_${opts.from}_a_${opts.to}.xlsx`);
  },

  recentVisits: async (opts: { term?: string; page?: number; size?: number } = {}): Promise<{ visits: VisitFeedRow[]; total: number }> => {
    const p = new URLSearchParams();
    if (opts.term) p.set('term', opts.term);
    p.set('page', String(opts.page || 1));
    p.set('size', String(opts.size || 20));
    const r = await baseApi.get<{ items: any[]; total: number }>(`/analytics/recent-visits?${p}`);
    return { visits: (r.items || []).map(mapVisitRow), total: r.total || 0 };
  },

  // ── Segmentos (Inc7) ── type ∈ best | inactive | dissatisfied | birthdays
  segment: async (type: 'best' | 'inactive' | 'dissatisfied' | 'birthdays', opts: { days?: number; limit?: number } = {}): Promise<UIClient[]> => {
    const p = new URLSearchParams();
    if (opts.days) p.set('days', String(opts.days));
    if (opts.limit) p.set('limit', String(opts.limit));
    const r = await baseApi.get<{ items: any[] }>(`/analytics/lists/${type}?${p}`);
    return (r.items || []).map(mapSegmentItem);
  },

  // ── Alta de cliente (Inc6) ── crea contacto; puntos iniciales opcionales vía checkin/adjust.
  create: async (data: { visitorName: string; phoneNumber: string; email?: string; birthDay?: number; birthMonth?: number; tags?: string[] }): Promise<{ id: string }> => {
    const [first_name, ...rest] = data.visitorName.trim().split(/\s+/);
    const last_name = rest.join(' ') || null;
    let birthdate: string | null = null;
    if (data.birthMonth && data.birthDay) birthdate = `2000-${String(data.birthMonth).padStart(2, '0')}-${String(data.birthDay).padStart(2, '0')}`;
    const r = await baseApi.post<{ id: number }>('/contacts', {
      first_name, last_name, phone: data.phoneNumber.replace(/\D/g, ''),
      email: data.email || null, birthdate, source: 'manager', tags: data.tags || [],
    });
    return { id: String(r.id) };
  },

  update: (id: string, data: Partial<{ visitorName: string; phoneNumber: string; email: string; birthDay: number; birthMonth: number }>) => {
    const body: any = {};
    if (data.visitorName != null) {
      const [first_name, ...rest] = data.visitorName.trim().split(/\s+/);
      body.first_name = first_name; body.last_name = rest.join(' ') || null;
    }
    if (data.phoneNumber != null) body.phone = data.phoneNumber.replace(/\D/g, '');
    if (data.email !== undefined) body.email = data.email || null;
    if (data.birthMonth && data.birthDay) body.birthdate = `2000-${String(data.birthMonth).padStart(2, '0')}-${String(data.birthDay).padStart(2, '0')}`;
    return baseApi.put(`/contacts/${id}`, body);
  },

  remove: (id: string) => baseApi.del(`/contacts/${id}`),

  // ── Operaciones de puntos (Inc6) — SERVER-SIDE ATÓMICO ──
  // Registrar VISITA real (mismo motor que el kiosko): visits=puntos fijos del programa;
  // cashback=% del monto de venta. Actualiza última visita, respeta el candado de tiempo
  // entre visitas (was_balance_query=true si cayó dentro), aplica cumpleaños automático y
  // dispara el mensaje de visita (o bienvenida si el contacto es nuevo).
  // FIX 2026-07-04: mandaba transaction_amount pero el backend lee `amount` (venta de $0).
  // birth_month va 0-INDEXADO (paridad kiosko/getUTCMonth).
  checkin: (args: { contactId?: string; phone?: string; firstName?: string; branchId?: string; amount?: number; birthday?: boolean; gender?: string; email?: string; birthDay?: number; birthMonth0?: number; occurredAt?: string; ticketRef?: string; source?: 'manager' | 'scan'; referredByPhone?: string; referredByCode?: string }) =>
    baseApi.post<{ is_new: boolean; contact_id: number; balance: number; points_added: number; was_balance_query?: boolean; birthday_applied?: boolean; mode?: string }>('/loyalty/checkin', {
      contact_id: args.contactId, phone: args.phone, first_name: args.firstName, branch_id: args.branchId,
      amount: args.amount ?? 0, birthday: !!args.birthday,
      gender: args.gender, email: args.email,
      birth_day: args.birthDay, birth_month: args.birthMonth0,
      occurred_at: args.occurredAt,  // backdate (YYYY-MM-DD); solo con el flag del negocio
      ticket_ref: args.ticketRef || undefined,  // folio del ticket del POS del negocio (10-oct-2026)
      source: args.source || 'manager',  // manager|scan: salta el candado de ticket POS (el operador es la caja) y marca el origen en el ledger
      // «¿Quién lo recomendó?» en el alta del panel (24-oct-2026, Hogar China City): el servidor lo procesa si es cliente nuevo.
      referred_by_phone: args.referredByPhone || undefined, referred_by_code: args.referredByCode || undefined,
    }) as Promise<{ is_new: boolean; contact_id: number; balance: number; points_added: number; was_balance_query?: boolean; birthday_applied?: boolean; mode?: string; referral?: { ok: boolean; reason?: string } }>,

  // Alta SIN venta + referido (24-oct-2026): el contacto ya existe, se acredita el referido aparte.
  addReferral: (args: { referredContactId: string; referrerPhone?: string; referrerCode?: string }) =>
    baseApi.post<{ ok: boolean; reason?: string }>('/loyalty/referrals', {
      referred_contact_id: args.referredContactId, referrer_phone: args.referrerPhone, referrer_code: args.referrerCode,
    }).catch((e: any) => (e?.data && typeof e.data.ok === 'boolean' ? e.data : { ok: false, reason: 'error' })),

  resolveReferralCode: (code: string) =>
    baseApi.get<{ found: boolean; contact?: { id: number; first_name: string } }>(`/loyalty/referrals/resolve?code=${encodeURIComponent(code)}`),

  // Ajuste manual ± (resta de puntos, correcciones). `points` firmado: + suma, - resta.
  // base usa operation (add/subtract) + magnitud → convertimos aquí.
  // notifyWhatsApp:false omite el aviso automático al cliente (correcciones internas).
  adjust: (args: { contactId: string; points: number; note?: string; notifyWhatsApp?: boolean; occurredAt?: string }) =>
    baseApi.post<{ balance: number }>('/loyalty/adjust', {
      contact_id: args.contactId,
      operation: args.points < 0 ? 'subtract' : 'add',
      points: Math.abs(args.points),
      note: args.note,
      occurred_at: args.occurredAt,
      notify_whatsapp: args.notifyWhatsApp === false ? false : undefined,
    }),

  // Canje de recompensa(s) (atómico, server-side). rewardIds = lista de ids. Dispara
  // la alerta de saldo bajo en el backend, así que el cliente no la maneja.
  redeem: (args: { contactId: string; rewardIds: string[]; branchId?: string | null }) =>
    baseApi.post<{ balance: number }>('/loyalty/redeem', {
      contact_id: args.contactId, reward_ids: args.rewardIds, branch_id: args.branchId || undefined,
    }),

  // ── Historial de un cliente (Inc6) — últimas visitas + ledger ──
  history: async (id: string, limit = 30): Promise<{ visits: any[]; total: number }> => {
    const r = await baseApi.get<{ visits: any[]; total: number }>(`/loyalty/contacts/${id}/visits?limit=${limit}`);
    return r;
  },

  // ── Referidos (Inc7) ──
  referrals: (contactId?: string) =>
    baseApi.get<{ referrals: any[] }>(`/loyalty/referrals${contactId ? `?contact_id=${contactId}` : ''}`),
  setReferralCode: (id: string, code: string) => baseApi.put(`/loyalty/contacts/${id}/referral-code`, { code }),
  removeReferralCode: (id: string) => baseApi.del(`/loyalty/contacts/${id}/referral-code`),
};
